<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import { ref } from "vue";
import {
  useToast,
  AtomCard,
  AtomText,
  AtomIcon,
  SatButton,
} from "@chatfood/design-system";
import { I18nTranslate } from "../i18n-translate";
import type { EnableLocationEmits } from "./emits";
import { useLocale } from "~/composables/use-locale";

const css = bemBuilder("enable-location");

const emits = defineEmits<EnableLocationEmits>();

const { t } = useLocale();
const { toastWarning } = useToast();

const isLocationLoading = ref(false);

function findPosition(): Promise<GeolocationPosition> | undefined {
  const geolocation = window.navigator?.geolocation;

  if (!geolocation) return;

  return new Promise((resolve, reject) => {
    geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
    });
  });
}

const onEnableLocation = async () => {
  if (isLocationLoading.value) return;

  isLocationLoading.value = true;

  try {
    const res = await findPosition();

    if (!res) {
      toastWarning(t("components.enable_location.warning_message_generic"));
      return;
    }

    emits("geoLocationObtained", {
      lat: res.coords.latitude,
      lng: res.coords.longitude,
    });
  } catch {
    toastWarning(t("components.enable_location.warning_message_blocked"));
  } finally {
    isLocationLoading.value = false;
  }
};
</script>

<template>
  <div :class="css()">
    <AtomCard radii="small">
      <AtomText color="color-text-secondary" type="small">
        <I18nTranslate keypath="components.enable_location.message">
          <template #highlight>
            <strong>
              {{ t("components.enable_location.message_bold") }}
            </strong>
          </template>
        </I18nTranslate>
      </AtomText>
    </AtomCard>
    <SatButton
      wide
      size="large"
      :class="css('cta')"
      :is-loading="isLocationLoading"
      @click="onEnableLocation"
    >
      <AtomIcon name="location" size="large" ignore-color />
      {{ t("components.enable_location.cta") }}
    </SatButton>
  </div>
</template>

<style lang="scss" scoped>
.enable-location {
  display: flex;
  text-align: center;
  flex-direction: column;
  gap: var(--spacing-xs);

  &__cta {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
  }
}
</style>
